import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Database, Brain, Share2, Clock, Trash2, Shield, Building, Mail } from "lucide-react";

export default function DataUsePolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "March 2, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#121212] text-white py-6 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <span className="text-[#121212] font-bold text-xl">F</span>
            </div>
            <span className="text-2xl font-bold font-['Manrope']">FinAI</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms</Link>
            <Link to="/login" className="bg-[#BFFF00] text-[#121212] px-4 py-2 rounded-full font-medium hover:bg-[#a8e600]">Login</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#F7FFE5] to-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Database className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-[#121212] font-['Manrope'] mb-4">Data Use Policy</h1>
          <p className="text-gray-600 text-lg">How we collect, process, and protect your information</p>
          <p className="text-gray-500 mt-2">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-gray-700 leading-relaxed">
              This Data Use Policy explains how Revurge Corporation ("we," "us," or "our") collects, uses, processes, and shares data when you use FinAI by Revurge. This policy supplements our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>.
            </p>
          </section>

          {/* Data We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-2">
              <Database className="w-6 h-6 text-[#BFFF00]" />
              Data Categories We Collect
            </h2>
            
            <div className="grid gap-4">
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-[#121212] mb-3">Account & Identity Data</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <ul className="space-y-1">
                    <li>• Full legal name</li>
                    <li>• Email address</li>
                    <li>• Phone number</li>
                    <li>• Physical/mailing address</li>
                  </ul>
                  <ul className="space-y-1">
                    <li>• Password (encrypted)</li>
                    <li>• Profile photo (optional)</li>
                    <li>• Date of birth</li>
                    <li>• Government ID (for verification)</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h3 className="font-semibold text-[#121212] mb-3">Business & Financial Data</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <ul className="space-y-1">
                    <li>• <strong>Employer Identification Number (EIN)</strong></li>
                    <li>• Business name and structure</li>
                    <li>• Industry classification</li>
                    <li>• Business registration documents</li>
                  </ul>
                  <ul className="space-y-1">
                    <li>• <strong>Bank account information</strong></li>
                    <li>• Transaction histories</li>
                    <li>• Payment card data</li>
                    <li>• Tax documents (W-2, 1099, etc.)</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-[#121212] mb-3">Technical & Usage Data</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <ul className="space-y-1">
                    <li>• IP address</li>
                    <li>• Device type and identifiers</li>
                    <li>• Browser type and version</li>
                    <li>• Operating system</li>
                  </ul>
                  <ul className="space-y-1">
                    <li>• Pages visited and features used</li>
                    <li>• Click patterns and navigation</li>
                    <li>• Time spent on platform</li>
                    <li>• Error logs and diagnostics</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* How We Use Data */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-2">
              <Brain className="w-6 h-6 text-[#BFFF00]" />
              How We Use Your Data
            </h2>
            
            <div className="space-y-4">
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Service Delivery</h4>
                <p className="text-sm text-gray-600">Processing transactions, generating invoices, syncing bank accounts, preparing tax documents, and providing bookkeeping services.</p>
              </div>
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">AI Processing</h4>
                <p className="text-sm text-gray-600">Document analysis, transaction categorization, expense classification, anomaly detection, and generating financial insights using artificial intelligence.</p>
              </div>
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Security & Compliance</h4>
                <p className="text-sm text-gray-600">Identity verification (KYC), fraud prevention, regulatory compliance, audit trail maintenance, and security monitoring.</p>
              </div>
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Communication</h4>
                <p className="text-sm text-gray-600">Sending service notifications, security alerts, payment confirmations, and customer support communications.</p>
              </div>
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Product Improvement</h4>
                <p className="text-sm text-gray-600">Analyzing usage patterns to improve features, fix bugs, and enhance user experience (using anonymized data where possible).</p>
              </div>
            </div>
          </section>

          {/* AI Usage */}
          <section className="mb-12 bg-purple-50 border border-purple-200 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-600" />
              Use of Artificial Intelligence
            </h2>
            <p className="text-gray-700 mb-4">
              We use AI and machine learning to enhance our services. Here's how your data is used with AI:
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li><strong>Document Processing:</strong> AI analyzes uploaded documents to extract data, categorize content, and identify key information.</li>
              <li><strong>Transaction Analysis:</strong> Machine learning categorizes transactions and identifies patterns or anomalies.</li>
              <li><strong>Natural Language Processing:</strong> AI interprets document content and extracts structured data.</li>
              <li><strong>Predictive Analytics:</strong> AI generates financial forecasts and recommendations based on historical data.</li>
            </ul>
            <div className="bg-white rounded-xl p-4 mt-4">
              <p className="text-sm text-gray-700">
                <strong>Important:</strong> AI processing is performed on secure, encrypted systems. Your data is not used to train general-purpose AI models. AI-generated outputs should be verified by qualified professionals.
              </p>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-2">
              <Share2 className="w-6 h-6 text-[#BFFF00]" />
              Data Sharing & Third Parties
            </h2>
            
            <p className="text-gray-700 mb-4">We share data only when necessary and with appropriate safeguards:</p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Recipient</th>
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Data Shared</th>
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Stripe</td>
                    <td className="px-4 py-3 text-gray-600">Payment information</td>
                    <td className="px-4 py-3 text-gray-600">Process payments securely</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Plaid</td>
                    <td className="px-4 py-3 text-gray-600">Bank credentials (encrypted)</td>
                    <td className="px-4 py-3 text-gray-600">Connect bank accounts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Cloud Providers</td>
                    <td className="px-4 py-3 text-gray-600">Encrypted data storage</td>
                    <td className="px-4 py-3 text-gray-600">Secure infrastructure</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Tax Authorities</td>
                    <td className="px-4 py-3 text-gray-600">Tax filings (upon your request)</td>
                    <td className="px-4 py-3 text-gray-600">Regulatory compliance</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Your Bookkeeper</td>
                    <td className="px-4 py-3 text-gray-600">Financial data (assigned)</td>
                    <td className="px-4 py-3 text-gray-600">Bookkeeping services</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
              <p className="text-sm text-red-800">
                <strong>We Never:</strong> Sell your personal data to third parties, share your financial information for marketing purposes, or provide access to unauthorized parties.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-2">
              <Clock className="w-6 h-6 text-[#BFFF00]" />
              Data Retention
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Data Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Retention Period</th>
                    <th className="px-4 py-3 text-left font-semibold text-[#121212]">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Account Data</td>
                    <td className="px-4 py-3 text-gray-600">Active use + 90 days</td>
                    <td className="px-4 py-3 text-gray-600">Service delivery</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Financial Records</td>
                    <td className="px-4 py-3 text-gray-600">7 years</td>
                    <td className="px-4 py-3 text-gray-600">IRS requirements</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Tax Documents</td>
                    <td className="px-4 py-3 text-gray-600">7 years</td>
                    <td className="px-4 py-3 text-gray-600">Regulatory compliance</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Transaction Logs</td>
                    <td className="px-4 py-3 text-gray-600">5 years</td>
                    <td className="px-4 py-3 text-gray-600">Audit purposes</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Security Logs</td>
                    <td className="px-4 py-3 text-gray-600">3 years</td>
                    <td className="px-4 py-3 text-gray-600">Security investigations</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Data Deletion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-6 flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-[#BFFF00]" />
              Data Deletion
            </h2>
            <p className="text-gray-700 mb-4">
              You have the right to request deletion of your data. Upon receiving a valid deletion request:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>We will delete or anonymize your personal data within 30 days</li>
              <li>Some data may be retained for legal compliance (financial records, tax documents)</li>
              <li>Backups are purged within 90 days of deletion</li>
              <li>You will receive confirmation once deletion is complete</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To request data deletion, contact us at <a href="mailto:compliance@revurge.com" className="text-blue-600 hover:underline">compliance@revurge.com</a>
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-12 bg-[#F7FFE5] border border-[#BFFF00]/30 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#121212]" />
              Your Data Rights
            </h2>
            <p className="text-gray-700 mb-4">Depending on your location, you may have the right to:</p>
            <div className="grid md:grid-cols-2 gap-4">
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• <strong>Access</strong> your personal data</li>
                <li>• <strong>Correct</strong> inaccurate data</li>
                <li>• <strong>Delete</strong> your data (with exceptions)</li>
                <li>• <strong>Export</strong> your data in a portable format</li>
              </ul>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• <strong>Restrict</strong> processing of your data</li>
                <li>• <strong>Object</strong> to certain uses of your data</li>
                <li>• <strong>Withdraw consent</strong> where applicable</li>
                <li>• <strong>Lodge complaints</strong> with authorities</li>
              </ul>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#BFFF00]" />
              Contact Us
            </h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Building className="w-6 h-6 text-[#121212] mt-1" />
                <div>
                  <p className="font-semibold text-[#121212]">Revurge Corporation</p>
                  <p className="text-gray-600">8 The Green, Dover, DE 19901</p>
                  <p className="text-gray-600 mt-2">
                    <strong>Data Requests:</strong> <a href="mailto:compliance@revurge.com" className="text-blue-600 hover:underline">compliance@revurge.com</a>
                  </p>
                  <p className="text-gray-600">
                    <strong>General:</strong> <a href="mailto:info@revurge.com" className="text-blue-600 hover:underline">info@revurge.com</a>
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#121212] text-white py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms of Service</Link>
            <Link to="/data-use" className="hover:text-[#BFFF00]">Data Use Policy</Link>
            <Link to="/cookies" className="hover:text-[#BFFF00]">Cookie Policy</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/faq" className="hover:text-[#BFFF00]">FAQ</Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">
            © {new Date().getFullYear()} Revurge Corporation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
